import React from 'react';

import ChevronLeft from '@/assets/icon-chevron-left.svg?react';
import { scrollRootToTop } from '@/core/utils/ui';

import classes from './styles.module.scss';

type Theme =
  | 'primary'
  | 'textOnPrimary'
  | 'outlineOnPrimary'
  | 'outlineOnBackground'
  | 'primaryOnWhite'
  | 'blackOnWhite'
  | 'black'
  | 'outlinePrimaryOnWhite'
  | 'outlineBlack';
type Size = 'small' | 'medium' | 'large';
type ChevronType = 'left' | 'right';

interface Props extends React.HTMLAttributes<HTMLElement> {
    onclick?: () => void;
    extClasses?: string;
    type: Theme;
    size: Size;
    children: React.ReactNode;
    style?: React.CSSProperties;
    disabled?: boolean;
    testId?: string;
    chevron?: ChevronType;
    disableScroll?: boolean;
    htmlType?: 'button' | 'submit' | 'reset';
    'data-i18n-key'?: string;
    // Forwarded for Automate's inspector and preview overlay, like data-i18n-key.
    // The i18n-data-attr and rs-setting-data-attr build plugins stamp these onto
    // the JSX and settingsTextMarker resolves them at runtime — but a component
    // that does not destructure them drops them silently, so the phrase reaches
    // the DOM untagged and cannot be overridden. That is exactly what hid the
    // details-modal pay buttons from snippet translations.
    'data-rs-setting-dynamic'?: string;
}

export default function Button({
    children,
    style,
    onclick,
    extClasses,
    type,
    size,
    disabled,
    testId,
    chevron,
    disableScroll,
    htmlType = 'button',
    'data-i18n-key': dataI18nKey,
    'data-rs-setting-dynamic': dataRsSettingDynamic,
}: Props) {
    const classNames = [
        classes.root, // base styles
        classes[type],
        classes[size],
        chevron === 'left' && classes.chevronLeft,
        extClasses,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            // eslint-disable-next-line react/button-has-type
            type={htmlType}
            // eslint-disable-next-line jsx-a11y/aria-role
            role="ui-button"
            style={style}
            disabled={disabled}
            className={classNames}
            onClick={() => {
                if (onclick) {
                    onclick();
                }
                if (!disableScroll) {
                    scrollRootToTop();
                }
            }}
            data-testid={testId}
            data-i18n-key={dataI18nKey}
            data-rs-setting-dynamic={dataRsSettingDynamic}
        >
            {chevron === 'left' && <ChevronLeft />}
            {children}
        </button>
    );
}
